// AuthContext - SSO authentication state management
// Implements SSO integration with insur_tale Toolbox

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { storeToken, getToken as getStoredToken, clearToken } from '../utils/tokenStorage';

interface User {
  id: string;
  displayName: string;
  email: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  logout: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // PREVENT double execution in StrictMode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * CHECK for SSO token on mount
   * Priority: URL parameter > localStorage
   */
  const checkAuth = async () => {
    try {
      // CHECK URL for token parameter (from Toolbox)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');

      if (urlToken) {
        console.log('🔑 SSO token detected in URL, validating...');
        await validateAndStoreToken(urlToken);

        // CLEANUP URL (remove token from browser history)
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // RESTORE user session from sessionStorage without re-validating
        // Keycloak JWTs expire quickly (5-15 min), but our session tokens last 8 hours
        // We trust stored session until API calls fail with 401
        const storedToken = getStoredToken();
        const storedUser = sessionStorage.getItem('arbiter_user');

        if (storedToken && storedUser) {
          console.log('🔑 Restoring stored session...');
          try {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          } catch (e) {
            console.error('Failed to restore session:', e);
            logout();
          }
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * VALIDATE token with backend
   * Stores token and user info if valid
   */
  const validateAndStoreToken = async (tokenValue: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(
        `${apiUrl}/api/auth/validate-token`,
        { token: tokenValue }
      );

      if (response.data.success) {
        console.log('✅ Token validated successfully');
        // STORE long-lived Arbiter session token (not the short-lived Keycloak JWT)
        const sessionToken = response.data.token;
        setToken(sessionToken);
        setUser(response.data.user);
        setIsAuthenticated(true);
        storeToken(sessionToken);
        sessionStorage.setItem('arbiter_user', JSON.stringify(response.data.user));
      }
    } catch (error: any) {
      console.error('❌ Token validation failed:', error.response?.data || error.message);
      logout(); // Clear invalid token
    }
  };

  /**
   * LOGOUT user
   * Clears token from state and localStorage
   */
  const logout = () => {
    console.log('👋 Logging out...');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    clearToken();
    sessionStorage.removeItem('arbiter_user');
  };

  /**
   * GET current token
   * Used by API client to add Authorization header
   */
  const getToken = () => token;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        loading,
        logout,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * HOOK to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
